import { openPanel } from "../helpers/utilities";
import lazyLoader from "./lazyLoader";

interface IMenuDropdownConfig {
  nodeLists: IMenuDropdownNodeLists,
  selectors: IMenuDropdownSelectors,
  htmlElements: IMenuDropdownHTMLElements,
  transitionDuration: number,
  slideout: Slideout,
  transitioning: boolean,
  isOpen: boolean,
  activePanel: HTMLElement,
  target: HTMLElement,
  previousPanel: HTMLElement
}

interface IMenuDropdownNodeLists {
  main: NodeList,
  panel: NodeList
}

interface IMenuDropdownHTMLElements {
  header: HTMLElement
}

interface IMenuDropdownSelectors {
  main: string,
  panel: string,
  header: string
}

class MenuDropdown {

  private config: IMenuDropdownConfig;

  constructor() {
    this.config = {
      activePanel: null,
      previousPanel: null,
      target: null,
      isOpen: false,
      transitioning: false,
      slideout: null,
      transitionDuration: 200,
      htmlElements: {
        header: null
      },
      nodeLists: {
        main: null,
        panel: null
      },
      selectors: {
        main: '[data-menu-dropdown]',
        panel: '[data-menu-dropdown-panel]',
        header: '[data-header]'
      }
    }
  }

  public init() {
    this.load();
    this.addEventListeners();
  }

  public reinit() {
    this.load();
  }

  private addEventListeners() {
    this.config.nodeLists.panel.forEach((htmlElement : HTMLElement) => htmlElement.addEventListener('mouseleave', this.closeAll.bind(this)));
    document.addEventListener('mouseover', this.detectMouseOverElement.bind(this));
  }

  private load() {
    this.config.nodeLists.main = document.querySelectorAll(this.config.selectors.main);
    this.config.nodeLists.panel = document.querySelectorAll(this.config.selectors.panel);
    this.config.htmlElements.header = document.querySelector(this.config.selectors.header);
  }

  private open(htmlElement: HTMLElement, delay?: number) {
    this.closeAll();

    return new Promise((resolve, reject) => {
      setTimeout(function() {
        openPanel(htmlElement, true);
        resolve();
        this.config.isOpen = true;  
      }.bind(this, htmlElement), delay);
    });
  }

  private close(htmlElement: HTMLElement, delay?: number) {
    return new Promise((resolve, reject) => {
      setTimeout(function() {
        openPanel(htmlElement, false);
        this.config.isOpen = false;
        resolve();
      }.bind(this, htmlElement), delay);
    });
  }

  private closeAll() {
    this.config.nodeLists.panel.forEach((panel: HTMLElement) => this.close(panel, 0));
  }

  private async detectMouseOverElement(event: Event) {
    const target = event.target as HTMLElement;

    if (this.config.target !== target) {
      await this.update(target);
    }
    
    this.config.target = target;
  }

  private async update (target: HTMLElement) {
    const main = target.closest(this.config.selectors.main) as HTMLElement;
    const panel = main ? this.getActivePanel(main) : target.closest(this.config.selectors.panel) as HTMLElement;
    const promoBar = target.closest('[data-promo-bar]');
    const isHovered = panel || main || promoBar ? true : false;

    if (isHovered) {
      this.config.previousPanel = this.config.activePanel !== panel ? this.config.activePanel : null;
      this.config.activePanel = main ? this.getActivePanel(main) : panel;
    }

    if (main && isHovered && this.config.previousPanel) {
      await this.close(this.config.previousPanel, 0);
      await this.open(this.config.activePanel, 200);
    }

    if (main && isHovered) {
      await this.open(this.config.activePanel, 0);
    }

    if (!isHovered) {
      this.closeAll();
      this.config.previousPanel = null;
      this.config.activePanel = null;
    }

    return Promise.resolve();
  }

  private getActivePanel(htmlElement: HTMLElement): HTMLElement {
    return Array.from(this.config.nodeLists.panel).find((panel: HTMLElement) => panel.dataset.menuDropdownPanel === htmlElement.dataset.menuDropdown) as HTMLElement;
  }
}

export default new MenuDropdown();